import axios from "axios";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
	alpha,
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	LinearProgress,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import ThemedDialog from "../../../Components/ThemedDialog";
import { FileReference, VehicleAssessment } from "../../../system/Domain";

interface Props {
	open: boolean;
	assessmentId: string;
	initialMarket1Price?: number | null;
	initialMarket2Price?: number | null;
	onClose: () => void;
	onSaved: (assessment: VehicleAssessment) => void;
}

const ManualDatValuationDialog = ({
	open,
	assessmentId,
	initialMarket1Price,
	initialMarket2Price,
	onClose,
	onSaved,
}: Props) => {
	const theme = useTheme();

	const [market1Price, setMarket1Price] = useState<string>(
		initialMarket1Price != null ? String(initialMarket1Price) : ""
	);
	const [market2Price, setMarket2Price] = useState<string>(
		initialMarket2Price != null ? String(initialMarket2Price) : ""
	);
	const [uploadedFile, setUploadedFile] = useState<FileReference | null>(null);
	const [uploadFilename, setUploadFilename] = useState<string>("");
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	const onDrop = useCallback(async (acceptedFiles: File[]) => {
		const file = acceptedFiles[0];
		if (!file) return;

		setUploadError(null);
		setUploadedFile(null);
		setUploadFilename(file.name);
		setUploadProgress(0);

		const formData = new FormData();
		formData.append("file", file);

		try {
			const { data } = await axios.post<FileReference>("/api/storage", formData, {
				headers: { "Content-Type": "multipart/form-data" },
				onUploadProgress: (event) => {
					const completed = Math.ceil((event.loaded / event.total) * 100);
					setUploadProgress(completed);
				},
			});
			setUploadedFile(data);
		} catch (e) {
			setUploadError(e.response?.data ?? e.message ?? "Upload fehlgeschlagen");
			setUploadProgress(null);
		}
	}, []);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: false,
		noKeyboard: true,
		noDragEventsBubbling: true,
		accept: { "application/pdf": [".pdf"] },
	});

	const isValid =
		market1Price !== "" &&
		market2Price !== "" &&
		!isNaN(Number(market1Price)) &&
		!isNaN(Number(market2Price)) &&
		uploadedFile !== null;

	const handleSave = async () => {
		if (!isValid) return;

		setSaving(true);
		setSubmitError(null);

		try {
			const { data } = await axios.post<VehicleAssessment>(
				`/api/assessments/${assessmentId}/manual-dat-valuation`,
				{
					market1Price: Number(market1Price),
					market2Price: Number(market2Price),
					fileHash: uploadedFile!.hash,
				}
			);
			onSaved(data);
		} catch (e) {
			setSubmitError(e.response?.data ?? e.message ?? "Speichern fehlgeschlagen");
		} finally {
			setSaving(false);
		}
	};

	const dropzoneBorderColor = isDragActive
		? alpha(theme.palette.primary.main, 0.6)
		: uploadError
		? alpha(theme.palette.error.light, 0.75)
		: alpha(theme.palette.primary.main, 0.3);

	const dropzoneColor = uploadError
		? theme.palette.error.light
		: alpha(theme.palette.primary.main, 0.5);

	return (
		<ThemedDialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
			<DialogTitle>Manuelle DAT Bewertung</DialogTitle>
			<DialogContent>
				<Box display="flex" flexDirection="column" gap={3} mt={1}>
					<Box display="flex" gap={2}>
						<TextField
							label="Händlereinkaufspreis (Market1Price)"
							type="number"
							value={market1Price}
							onChange={(e) => setMarket1Price(e.target.value)}
							InputProps={{
								endAdornment: <InputAdornment position="end">€</InputAdornment>,
							}}
							fullWidth
							variant="standard"
						/>
						<TextField
							label="Händlerverkaufspreis (Market2Price)"
							type="number"
							value={market2Price}
							onChange={(e) => setMarket2Price(e.target.value)}
							InputProps={{
								endAdornment: <InputAdornment position="end">€</InputAdornment>,
							}}
							fullWidth
							variant="standard"
						/>
					</Box>

					<Box>
						<Box
							{...getRootProps()}
							sx={{
								border: "2px dashed",
								borderColor: dropzoneBorderColor,
								borderRadius: 2,
								width: "100%",
								minHeight: 100,
								cursor: "pointer",
								color: dropzoneColor,
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
								alignItems: "center",
								textAlign: "center",
								padding: 2,
								transition: "border 200ms ease-out, color 200ms ease-out",
								outline: "none",
							}}
						>
							<input {...getInputProps()} />
							{uploadedFile ? (
								<Typography variant="body2" color="success.main">
									✓ {uploadFilename}
								</Typography>
							) : uploadProgress !== null && !uploadError ? (
								<Box width="100%">
									<Typography variant="body2" gutterBottom>
										{uploadFilename}
									</Typography>
									<LinearProgress variant="determinate" value={uploadProgress} />
								</Box>
							) : (
								<>
									<Typography variant="body2">
										PDF-Datei per Drag & Drop ablegen oder klicken zum Auswählen
									</Typography>
									<Typography variant="caption" sx={{ opacity: 0.6 }}>
										(nur PDF)
									</Typography>
								</>
							)}
						</Box>
						{uploadError && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
								{uploadError}
							</Typography>
						)}
					</Box>

					{submitError && (
						<Typography variant="body2" color="error">
							{submitError}
						</Typography>
					)}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" color="secondary" onClick={onClose} href="" disabled={saving}>
					Abbrechen
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handleSave}
					href=""
					disabled={!isValid || saving}
				>
					Speichern
				</Button>
			</DialogActions>
		</ThemedDialog>
	);
};

export default ManualDatValuationDialog;
